import React, { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const ZURICH_CENTER = [47.3769, 8.5417];
const ZURICH_ZOOM = 11;

const defaultMarkerIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

function FitMapToLocations({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(locations) || locations.length === 0) {
      map.setView(ZURICH_CENTER, ZURICH_ZOOM);
      return;
    }
    const bounds = L.latLngBounds(
      locations.map((location) => [location.lat, location.lng]),
    );
    map.fitBounds(bounds, { padding: [36, 36], maxZoom: 14 });
  }, [locations, map]);

  return null;
}

export default function DiscoveryMapView({
  locations,
  mode,
  isLoading,
  error,
  missingCoordsCount = 0,
  onRetry,
  onBackToList,
  onPinOpen,
}) {
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!Array.isArray(locations) || locations.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && locations.some((location) => location.id === prev)) return prev;
      return locations[0].id;
    });
  }, [locations]);

  const selectedLocation = useMemo(
    () =>
      Array.isArray(locations)
        ? locations.find((location) => location.id === selectedId) ?? null
        : null,
    [locations, selectedId],
  );

  const emptyLabel =
    mode === "all"
      ? "Keine Orte im Katalog gefunden."
      : "Keine Orte fuer diese Filter. Lockere die Filter oder wechsle zu 'Alle Orte im Katalog'.";

  return (
    <section className="discovery-map-view" aria-label="Kartenansicht">
      {isLoading ? (
        <div className="discovery-map-placeholder">Karte wird geladen…</div>
      ) : error ? (
        <div className="discovery-map-placeholder">
          <p>{error}</p>
          {onRetry ? (
            <button
              type="button"
              className="homepage-column-filter-btn"
              onClick={onRetry}
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      ) : !Array.isArray(locations) || locations.length === 0 ? (
        <div className="discovery-map-placeholder">
          <p>{emptyLabel}</p>
        </div>
      ) : (
        <>
          <div className="discovery-map-canvas">
            <MapContainer
              center={ZURICH_CENTER}
              zoom={ZURICH_ZOOM}
              scrollWheelZoom={true}
              className="discovery-map-leaflet"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitMapToLocations locations={locations} />
              {locations.map((location) => (
                <Marker
                  key={location.id}
                  position={[location.lat, location.lng]}
                  icon={defaultMarkerIcon}
                  eventHandlers={{
                    click: () => {
                      setSelectedId(location.id);
                      onPinOpen?.(location);
                    },
                  }}
                >
                  <Tooltip direction="top" offset={[0, -24]} opacity={0.9}>
                    {location.name}
                    {location.activityCount != null ? ` (${location.activityCount})` : ""}
                  </Tooltip>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {selectedLocation ? (
            <aside className="discovery-map-sheet" aria-live="polite">
              <div className="discovery-map-sheet-title-row">
                <h3 className="discovery-map-sheet-title">{selectedLocation.name}</h3>
                {selectedLocation.activityCount != null ? (
                  <span className="discovery-map-count-chip">
                    {selectedLocation.activityCount} Aktivitaeten
                  </span>
                ) : null}
              </div>
              {selectedLocation.sampleActivities?.length ? (
                <ul className="discovery-map-sheet-list">
                  {selectedLocation.sampleActivities.map((activity) => (
                    <li key={activity.id}>{activity.title}</li>
                  ))}
                </ul>
              ) : (
                <p className="discovery-map-sheet-empty">
                  Fuer diesen Ort sind aktuell keine Vorschauen verfuegbar.
                </p>
              )}
              <div className="discovery-map-sheet-actions">
                <button
                  type="button"
                  className="entdecken-link-teilen-btn"
                  onClick={() => onBackToList?.(selectedLocation)}
                >
                  In Entdecken anzeigen
                </button>
              </div>
            </aside>
          ) : null}
          {missingCoordsCount > 0 ? (
            <p className="discovery-map-footnote">
              {missingCoordsCount} Aktivitaeten ohne Koordinaten werden nicht auf der Karte gezeigt.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
